import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static values = {
    title: String,
    text: String,
    url: String,
    payload: String,
    resourceId: Number,
    targetType: String,
    copiedToast: String,
    sentToast: String
  }

  async share(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.isSharing) return

    this.isSharing = true
    this.#setBusy(true)
    this.#pop()

    try {
      let toastMessage = this.copiedToastValue

      if (this.#canUseNativeShare()) {
        await navigator.share(this.#nativeShareData())
        toastMessage = this.sentToastValue
      } else {
        await navigator.clipboard.writeText(this.payloadValue)
      }

      this.#showToast(toastMessage)
      this.#closeMenu()
      await this.#logShare()
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Failed to share try:", error)
      }
    } finally {
      this.isSharing = false
      this.#setBusy(false)
    }
  }

  #canUseNativeShare() {
    if (typeof navigator.share !== "function") return false
    if (!this.#isMobileLike()) return false

    const data = {
      title: this.titleValue,
      text: this.payloadValue
    }

    return typeof navigator.canShare !== "function" || navigator.canShare(data)
  }

  #nativeShareData() {
    return {
      title: this.titleValue,
      text: this.payloadValue
    }
  }

  #isMobileLike() {
    return navigator.maxTouchPoints > 0 ||
      window.matchMedia?.("(pointer: coarse)")?.matches === true
  }

  #showToast(message) {
    document.querySelectorAll(".try-share-toast").forEach((toast) => toast.remove())

    const toast = document.createElement("div")
    toast.className = "try-fx-toast try-share-toast"
    toast.dataset.turboTemporary = ""
    const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    icon.setAttribute("width", "16")
    icon.setAttribute("height", "16")
    icon.setAttribute("viewBox", "0 0 24 24")
    icon.setAttribute("fill", "none")
    icon.setAttribute("stroke", "white")
    icon.setAttribute("stroke-width", "3")
    icon.setAttribute("stroke-linecap", "round")
    icon.setAttribute("stroke-linejoin", "round")
    icon.setAttribute("aria-hidden", "true")

    const checkmark = document.createElementNS("http://www.w3.org/2000/svg", "polyline")
    checkmark.setAttribute("points", "20 6 9 17 4 12")
    icon.appendChild(checkmark)

    const msg = document.createElement("span")
    msg.textContent = message
    toast.appendChild(icon)
    toast.appendChild(msg)
    document.body.appendChild(toast)

    setTimeout(() => {
      toast.classList.add("try-fx-toast--out")
      setTimeout(() => toast.remove(), 220)
    }, 1800)
  }

  #pop() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.element.classList.remove("try-fx-pop")
    void this.element.offsetWidth
    this.element.classList.add("try-fx-pop")
    setTimeout(() => this.element.classList.remove("try-fx-pop"), 580)
  }

  #closeMenu() {
    this.element.closest("details")?.removeAttribute("open")
  }

  async #logShare() {
    await post("/user_activities/activity", {
      body: JSON.stringify({
        event: "try.share",
        resource_id: this.resourceIdValue,
        target_type: this.targetTypeValue
      })
    })
  }

  #setBusy(isBusy) {
    this.element.setAttribute("aria-busy", String(isBusy))

    if ("disabled" in this.element) {
      this.element.disabled = isBusy
    }
  }
}
